<template>
  <div class="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg overflow-hidden">
    <div class="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-2">
        <UsersIcon class="h-5 w-5 text-purple-500" />
        <h3 class="text-lg font-medium text-gray-900 dark:text-white">Pipeline per Afdeling</h3>
        <button
          @click="showInfo = !showInfo"
          class="text-gray-400 hover:text-purple-500 dark:hover:text-purple-400 transition-colors"
          title="Uitleg over deze tabel"
        >
          <InformationCircleIcon class="h-5 w-5" />
        </button>
      </div>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Open deals, omzet en winkans per medewerker, gegroepeerd per team
      </p>
    </div>

    <InfoBox v-if="showInfo" title="Hoe lees je deze tabel?" color="purple">
      <p>
        De tabel toont alle <strong>open deals</strong> per medewerker, gegroepeerd per team. Per
        rij zie je hoeveel deals in elke salesfase zitten (Verkenning, Lead, Prospect) en de
        bijbehorende omzetcijfers.
      </p>
      <p>
        <strong>Gewogen (ML)</strong> — De dealwaarde vermenigvuldigd met de ML-voorspelde winkans.
        Dit geeft een realistischer beeld van de verwachte opbrengst dan de totale omzet.
      </p>
      <p>
        <strong>Winkans (ML)</strong> — De gemiddelde winkans van iemands open deals, voorspeld door
        een machine learning model (Logistic Regression, AUC 0.85). Het model weegt 11 factoren mee,
        waaronder dealwaarde, salesfase, doorlooptijd en de historische conversie van de medewerker.
      </p>
      <p>
        <strong>Conversie</strong> — Het historische winstpercentage van de medewerker: hoeveel van
        alle afgeronde deals daadwerkelijk gewonnen zijn. Bij minder dan 5 afgeronde deals wordt het
        percentage afgevlakt met het bedrijfsgemiddelde om vertekening te voorkomen.
      </p>
    </InfoBox>

    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead class="bg-gray-50 dark:bg-gray-700/50">
          <tr>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Naam
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Verkenning
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Lead
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Prospect
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Totale omzet
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Gewogen (ML)
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Winkans (ML)
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Conversie
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
          <template v-for="team in data.teams" :key="team.team_id">
            <!-- Team header row -->
            <tr
              class="bg-gray-100 dark:bg-gray-700 cursor-pointer hover:bg-gray-150 dark:hover:bg-gray-650"
              @click="toggleTeam(team.team_id)"
            >
              <td class="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white">
                <div class="flex items-center gap-2">
                  <ChevronRightIcon
                    class="h-4 w-4 transition-transform"
                    :class="{ 'rotate-90': expandedTeams.has(team.team_id) }"
                  />
                  {{ team.team_name }}
                  <span class="text-xs font-normal text-gray-500 dark:text-gray-400">
                    ({{ team.employees.length }})
                  </span>
                </div>
              </td>
              <td class="px-4 py-3 text-sm text-right font-semibold text-gray-900 dark:text-white">
                {{ team.subtotals.verkenning_count }}
              </td>
              <td class="px-4 py-3 text-sm text-right font-semibold text-gray-900 dark:text-white">
                {{ team.subtotals.lead_count }}
              </td>
              <td class="px-4 py-3 text-sm text-right font-semibold text-gray-900 dark:text-white">
                {{ team.subtotals.prospect_count }}
              </td>
              <td class="px-4 py-3 text-sm text-right font-semibold text-gray-900 dark:text-white">
                {{ formatCurrency(team.subtotals.total_revenue) }}
              </td>
              <td
                class="px-4 py-3 text-sm text-right font-semibold text-green-600 dark:text-green-400"
              >
                {{ formatCurrency(team.subtotals.weighted_revenue) }}
              </td>
              <td class="px-4 py-3 text-sm text-right font-semibold text-gray-900 dark:text-white">
                <span v-if="team.subtotals.avg_ml_probability != null">
                  {{ formatPercent(team.subtotals.avg_ml_probability) }}
                </span>
                <span v-else class="text-gray-400">-</span>
              </td>
              <td class="px-4 py-3 text-sm text-right font-semibold text-gray-900 dark:text-white">
                <span v-if="team.subtotals.avg_conversion_rate != null">
                  {{ formatPercent(team.subtotals.avg_conversion_rate) }}
                </span>
                <span v-else class="text-gray-400">-</span>
              </td>
            </tr>

            <!-- Employee rows -->
            <template v-if="expandedTeams.has(team.team_id)">
              <tr
                v-for="emp in team.employees"
                :key="emp.employee_id"
                class="hover:bg-gray-50 dark:hover:bg-gray-700/30"
              >
                <td class="px-4 py-2.5 text-sm text-gray-900 dark:text-white pl-10">
                  <div>
                    {{ emp.employee_name }}
                    <span
                      v-if="emp.employee_function"
                      class="text-xs text-gray-500 dark:text-gray-400 ml-1"
                    >
                      {{ emp.employee_function }}
                    </span>
                  </div>
                </td>
                <td class="px-4 py-2.5 text-sm text-right text-gray-700 dark:text-gray-300">
                  {{ emp.verkenning_count || '-' }}
                </td>
                <td class="px-4 py-2.5 text-sm text-right text-gray-700 dark:text-gray-300">
                  {{ emp.lead_count || '-' }}
                </td>
                <td class="px-4 py-2.5 text-sm text-right text-gray-700 dark:text-gray-300">
                  {{ emp.prospect_count || '-' }}
                </td>
                <td class="px-4 py-2.5 text-sm text-right text-gray-700 dark:text-gray-300">
                  {{ formatCurrency(emp.total_revenue) }}
                </td>
                <td
                  class="px-4 py-2.5 text-sm text-right font-medium text-green-600 dark:text-green-400"
                >
                  {{ formatCurrency(emp.weighted_revenue) }}
                </td>
                <td
                  class="px-4 py-2.5 text-sm text-right font-medium text-gray-700 dark:text-gray-300"
                >
                  <span v-if="emp.avg_ml_probability != null">
                    {{ formatPercent(emp.avg_ml_probability) }}
                  </span>
                  <span v-else class="text-gray-400">-</span>
                </td>
                <td
                  class="px-4 py-2.5 text-sm text-right font-medium text-gray-700 dark:text-gray-300"
                >
                  <span v-if="emp.conversion_rate != null">
                    {{ formatPercent(emp.conversion_rate) }}
                  </span>
                  <span v-else class="text-gray-400">-</span>
                </td>
              </tr>
            </template>
          </template>
        </tbody>
        <tfoot class="bg-gray-50 dark:bg-gray-700/50">
          <tr>
            <td class="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white">Totaal</td>
            <td class="px-4 py-3 text-sm text-right font-bold text-gray-900 dark:text-white">
              {{ data.totals.verkenning_count }}
            </td>
            <td class="px-4 py-3 text-sm text-right font-bold text-gray-900 dark:text-white">
              {{ data.totals.lead_count }}
            </td>
            <td class="px-4 py-3 text-sm text-right font-bold text-gray-900 dark:text-white">
              {{ data.totals.prospect_count }}
            </td>
            <td class="px-4 py-3 text-sm text-right font-bold text-gray-900 dark:text-white">
              {{ formatCurrency(data.totals.total_revenue) }}
            </td>
            <td class="px-4 py-3 text-sm text-right font-bold text-green-600 dark:text-green-400">
              {{ formatCurrency(data.totals.weighted_revenue) }}
            </td>
            <td class="px-4 py-3 text-sm text-right font-bold text-gray-900 dark:text-white">
              <span v-if="data.totals.avg_ml_probability != null">
                {{ formatPercent(data.totals.avg_ml_probability) }}
              </span>
              <span v-else class="text-gray-400">-</span>
            </td>
            <td class="px-4 py-3 text-sm text-right font-bold text-gray-900 dark:text-white">
              <span v-if="data.totals.avg_conversion_rate != null">
                {{ formatPercent(data.totals.avg_conversion_rate) }}
              </span>
              <span v-else class="text-gray-400">-</span>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { UsersIcon, ChevronRightIcon, InformationCircleIcon } from '@heroicons/vue/24/outline'
import InfoBox from './InfoBox.vue'

defineProps({
  data: { type: Object, required: true }
})

const showInfo = ref(false)
const expandedTeams = ref(new Set())

function toggleTeam(teamId) {
  if (expandedTeams.value.has(teamId)) {
    expandedTeams.value.delete(teamId)
  } else {
    expandedTeams.value.add(teamId)
  }
}

function formatCurrency(amount) {
  if (!amount) return '€ 0'
  return `€ ${new Intl.NumberFormat('nl-NL').format(Math.round(amount))}`
}

function formatPercent(value) {
  if (value == null) return '-'
  return `${Math.round(value * 100)}%`
}
</script>
